import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { format, isAfter, isBefore } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Chip,
  useMediaQuery,
  useTheme,
  TextField,
  Grid,
  Button,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  AccountBalanceWallet as WalletIcon,
  Receipt as ReceiptIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  FilterAlt as FilterIcon,
  Clear as ClearIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { BASE_URL } from "../../components/config/config";

const PaymentHistoryClient = () => {
  const clientState = useSelector((state) => state.clientReducer);
  const clientId = clientState.clientDetails?.client_id;
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        if (!clientId) {
          setLoading(false);
          return;
        }

        setLoading(true);
        const response = await fetch(
          `${BASE_URL}admin/admin-get-client-wallet-transactions?client_id=${clientId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to fetch transactions");
        }

        const data = await response.json();
        const formattedTransactions = data.data.map((transaction) => ({
          id: transaction._id,
          wallet_id: transaction.wallet_id,
          client_id: transaction.client_id,
          amount: transaction.amount || 0,
          date: transaction.createdAt,
          description: transaction.description || "No description",
          type: transaction.transaction_type?.toLowerCase() || "unknown",
          status: "completed",
          reference_id: transaction._id,
          ticket_id: transaction.ticket_id || "N/A",
          remaining_balance: transaction.remaining_balance || 0,
          transaction_type: transaction.transaction_type || "UNKNOWN",
          payment_method: transaction.payment_method || "N/A",
          processed_by: transaction.processed_by || "System",
        }));

        // Sort by latest first
        const sortedTransactions = formattedTransactions.sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );

        setTransactions(sortedTransactions);
        setFilteredTransactions(sortedTransactions);
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message || "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [clientId]);

  const handleFilter = () => {
    const filtered = transactions.filter((transaction) => {
      const txDate = new Date(transaction.date);
      const from = fromDate ? new Date(fromDate) : null;
      const to = toDate ? new Date(toDate) : null;

      if (from && isBefore(txDate, from)) return false;
      if (to && isAfter(txDate, to)) return false;

      return true;
    });

    setFilteredTransactions(filtered);
  };

  const handleClearFilters = () => {
    setFromDate("");
    setToDate("");
    setFilteredTransactions(transactions);
  };

  const toggleRowExpand = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const getTransactionIcon = (type) => {
    const lowerType = type?.toLowerCase();
    switch (lowerType) {
      case "added":
        return <ArrowDownIcon className="text-green-500" fontSize="small" />;
      case "deducted":
        return <ArrowUpIcon className="text-red-500" fontSize="small" />;
      default:
        return <ReceiptIcon className="text-blue-500" fontSize="small" />;
    }
  };

  const formatCurrency = (value) => {
    if (typeof value !== "number") {
      value = Number(value) || 0;
    }
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  if (loading) {
    return (
      <Box className="flex justify-center items-center min-h-[200px]">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="flex justify-center items-center min-h-[200px]">
        <Typography className="text-red-500">Error: {error}</Typography>
      </Box>
    );
  }

  if (!clientId) {
    return (
      <Box className="flex justify-center items-center min-h-[200px]">
        <Typography className="text-gray-500">No client selected</Typography>
      </Box>
    );
  }

  return (
    <div className="w-full overflow-hidden p-4">
      <div className="mb-6">
        <Typography
          variant="h5"
          component="h2"
          className="font-bold text-gray-800"
        >
          Payment History
        </Typography>

        {/* Date Filter - Always visible */}
        <Grid container spacing={2} className="mb-4 mt-4" alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              size="small"
              className="bg-white rounded-lg"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              size="small"
              className="bg-white rounded-lg"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} className="text-right mt-0 md:mt-0">
            <Grid container justifyContent="flex-end" spacing={2}>
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={
                    <FilterIcon
                      className={
                        fromDate || toDate ? "text-white" : "text-gray-600"
                      }
                    />
                  }
                  onClick={handleFilter}
                  disabled={!fromDate && !toDate}
                  className={`rounded-lg normal-case px-6 py-2 shadow-md 
                  ${
                    fromDate || toDate
                      ? "!bg-teal-600 hover:!bg-teal-800 !text-white"
                      : "!bg-gray-200 !text-gray-600"
                  }`}
                >
                  Filter
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                  disabled={!fromDate && !toDate}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg normal-case px-6 py-2"
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>

      {/* Show table only if there are transactions */}
      {filteredTransactions.length > 0 ? (
        <TableContainer
          component={Paper}
          className="rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
        >
          <Table aria-label="payment history table" className="min-w-full">
            <TableHead>
              <TableRow className="bg-gradient-to-r from-teal-600 to-teal-800">
                {[
                  "Date",
                  "Request ID",
                  "Description",
                  "Amount",
                  "Type",
                  "Balance",
                  "Details",
                ].map((header) => (
                  <TableCell
                    key={header}
                    className="!text-white font-semibold py-4 text-sm uppercase tracking-wide"
                    align={
                      header === "Amount" || header === "Balance"
                        ? "right"
                        : "left"
                    }
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredTransactions.map((transaction) => (
                <React.Fragment key={transaction.id}>
                  <TableRow className="hover:bg-gray-50 transition-all duration-200 hover:scale-[1.01] hover:shadow-sm">
                    {/* Date */}
                    <TableCell className="py-4">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">
                          {format(new Date(transaction.date), "MMM dd, yyyy")}
                        </span>
                        <span className="text-xs text-gray-500">
                          {format(new Date(transaction.date), "HH:mm:ss")}
                        </span>
                      </div>
                    </TableCell>

                    {/* Ticket ID */}
                    <TableCell className="py-4">
                      <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded-lg shadow-sm">
                        {transaction.ticket_id}
                      </span>
                    </TableCell>

                    {/* Description */}
                    <TableCell className="py-4">
                      <div className="flex items-center">
                        <div className="mr-3">
                          {getTransactionIcon(transaction.transaction_type)}
                        </div>
                        <Typography className="font-medium text-gray-800">
                          {transaction.description}
                        </Typography>
                      </div>
                    </TableCell>

                    {/* Amount */}
                    <TableCell className="py-4 text-right">
                      <Typography
                        className={`font-semibold ${
                          transaction.transaction_type.toLowerCase() === "added"
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {transaction.transaction_type.toLowerCase() === "added"
                          ? "+"
                          : "-"}{" "}
                        ₹{formatCurrency(transaction.amount)}
                      </Typography>
                    </TableCell>

                    {/* Type Chip */}
                    <TableCell className="py-4 text-center">
                      <Chip
                        label={transaction.transaction_type}
                        size="small"
                        className={`rounded-full px-4 py-1 text-xs font-medium shadow-sm ${
                          transaction.transaction_type.toLowerCase() === "added"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      />
                    </TableCell>

                    {/* Balance */}
                    <TableCell className="py-4 text-right">
                      <Typography className="font-semibold text-gray-800">
                        ₹{formatCurrency(transaction.remaining_balance)}
                      </Typography>
                    </TableCell>

                    {/* Expand Button */}
                    <TableCell className="py-4 text-center">
                      <Tooltip title="View details">
                        <IconButton
                          onClick={() => toggleRowExpand(transaction.id)}
                          className="text-teal-600 hover:bg-teal-50"
                          size="small"
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Row */}
                  {expandedRow === transaction.id && (
                    <TableRow className="bg-blue-50">
                      <TableCell colSpan={7} className="py-5 px-6 rounded-b-xl">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div className="p-4 bg-white rounded-xl shadow-sm">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">
                              Transaction Details
                            </h4>
                            <ul className="text-sm text-gray-600 space-y-1">
                              <li>
                                Reference ID:{" "}
                                <span className="font-mono">
                                  {transaction.reference_id}
                                </span>
                              </li>
                              <li>
                                Wallet ID:{" "}
                                <span className="font-mono">
                                  {transaction.wallet_id}
                                </span>
                              </li>
                              <li>
                                Payment Method:{" "}
                                <span className="capitalize">
                                  {transaction.payment_method}
                                </span>
                              </li>
                            </ul>
                          </div>

                          <div className="p-4 bg-white rounded-xl shadow-sm">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">
                              Processing Info
                            </h4>
                            <ul className="text-sm text-gray-600 space-y-1">
                              <li>Processed By: {transaction.processed_by}</li>
                              <li>
                                Status:{" "}
                                <span className="text-green-600 font-semibold">
                                  Completed
                                </span>
                              </li>
                            </ul>
                          </div>

                          <div className="p-4 bg-white rounded-xl shadow-sm">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">
                              Amount Details
                            </h4>
                            <ul className="text-sm text-gray-600 space-y-1">
                              <li>
                                Previous Balance: ₹
                                {formatCurrency(
                                  transaction.remaining_balance -
                                    transaction.amount
                                )}
                              </li>
                              <li>
                                Transaction Amount: ₹
                                {formatCurrency(transaction.amount)}
                              </li>
                              <li>
                                New Balance: ₹
                                {formatCurrency(transaction.remaining_balance)}
                              </li>
                            </ul>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        // Show message when no transactions found
        <Box className="flex flex-col items-center justify-center min-h-[200px] bg-gray-50 rounded-2xl p-8 border border-gray-200">
          <WalletIcon className="text-gray-400" style={{ fontSize: 48 }} />
          <Typography variant="h6" className="text-gray-500 mt-4 text-center">
            {transactions.length === 0
              ? "No transactions found for this client"
              : "No transactions match your filter criteria"}
          </Typography>
        </Box>
      )}
    </div>
  );
};

export default PaymentHistoryClient;
